import { createSelector } from '@reduxjs/toolkit';
import {
  OnBoardingFlowType,
  OnBoardingScreenOptions,
  OnBoardingScreenOptionType,
  OnBoardingScreensType,
} from '@mindly/ui-components';
import { RootState } from '../index';
import { ILocalizedAreasOfWorkByClientProblem } from 'src/pages/types';

export const onBoardingFlowSelector = (state: RootState): OnBoardingFlowType | null => state.onBoardingFlow.flowData;

export const isLoadingFlowSelector = (state: RootState): boolean => state.onBoardingFlow.isLoading;

export const flowErrorSelector = (state: RootState): string | null => state.onBoardingFlow.error;

export const flowProgressSelector = (
  state: RootState
): {
  progress: number;
  currentCount: number;
  totalCount: number;
} => state.onBoardingFlow.progressInfo;

export const flowSelectedAnswersSelector = (state: RootState): Record<string, string[]> =>
  state.onBoardingFlow.selectedAnswers;

export const flowSelectedAnswersByScreenSelector = (screenId: string) =>
  createSelector(onBoardingFlowSelector, flowSelectedAnswersSelector, (flowData, answers) => {
    const screenAnswers = answers[screenId];
    if (!screenAnswers?.length) return [];

    if (
      [OnBoardingScreenOptions.LIST_LOADER, OnBoardingScreenOptions.GOAL].includes(
        flowData?.screens[screenId]?.type as OnBoardingScreenOptions
      )
    ) {
      return screenAnswers;
    }

    const filteredAnswers = screenAnswers.map((answerId) => {
      // @ts-ignore
      return flowData?.screens[screenId]?.options?.find(
        (option: OnBoardingScreenOptionType) => option.id === answerId || option.filtrationAnswerId === answerId
      );
    });

    return filteredAnswers.filter(Boolean).map((option) => option.id);
  });

export const flowScreensHistorySelector = (state: RootState): string[] => state.onBoardingFlow.screensHistory;

export const flowCurrentScreenIdSelector = (state: RootState): string | null => state.onBoardingFlow.currentScreenId;

export const flowCurrentScreenDataSelector = createSelector(
  onBoardingFlowSelector,
  flowCurrentScreenIdSelector,
  (flowData, screenId): OnBoardingScreensType | undefined =>
    screenId && flowData?.screens?.[screenId] ? { ...flowData?.screens?.[screenId], id: screenId } : undefined
);

export const flowPrevScreenIdSelector = createSelector(
  flowScreensHistorySelector,
  flowCurrentScreenIdSelector,
  (screensHistory, screenId): string | null => {
    let id: string | null = null;

    if (!screenId) {
      return id;
    }

    const index = screensHistory.indexOf(screenId);
    if (index > 0) {
      id = screensHistory[index - 1];
    } else {
      id = null;
    }

    return id;
  }
);

export const flowIsTestingSelector = (state: RootState): boolean => state.onBoardingFlow.isTesting;

export const flowActualSelectedAnswersSelector = createSelector(
  flowSelectedAnswersSelector,
  flowScreensHistorySelector,
  onBoardingFlowSelector,
  (answers, history, flow) => {
    return history.reduce((acc, screenId) => {
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      const questionID = flow?.screens[screenId]?.filtrationQuestionId || screenId;
      const screenAnswers = answers[screenId];
      if (!screenAnswers) return acc;

      return { ...acc, [questionID]: screenAnswers };
    }, {});
  }
);

export const getUserOnBoardingFlowKeySelector = (state: RootState): string =>
  state?.onBoardingFlow.onboarding_key || '';

export const experimentVariantSelector = (state: RootState): { name: string; id: string } | null | undefined =>
  state?.onBoardingFlow?.selectedExperimentVariant;

export const flowUserEmailSelector = (state: RootState): string | null => state?.onBoardingFlow?.userEmail || null;

export const flowUseAdditionalPromotionalCodeDiscountSelector = (state: RootState): boolean | null =>
  state?.onBoardingFlow?.useAdditionalPromotionalCodeDiscount;

export const getLocalizedAreasOfWorkByClientProblemSelector = (
  state: RootState
): ILocalizedAreasOfWorkByClientProblem[] => state?.onBoardingFlow?.localizedAreasOfWorkByClientProblem || [];

export const getGoalScreenDataSelector = (
  state: RootState
): {
  currentGoalScreenCount: number;
  currentCategoryName: string;
  answers: Record<string, string[] | null>;
} => state?.onBoardingFlow?.goalScreenData || {};
